import {FIELD_BIOME_TEXTURES} from '../../../configs/fieldBiomeEffects';
import {WILL_O_WISPS_KIND} from '../../../configs/biomeEffects/willOWisps';
import {resolveSpawnBox} from './biomeContext';
import {
  TWO_PI,
  expandBox,
  lerp,
  pick,
  pickPointInBox,
  pickPointInOval,
  pickWeighted,
  randomIn,
  randomInt,
  randomRange,
  randomSign,
  smoothstep,
  toRadians,
  writeHiddenPose,
  writePose,
} from './biomeMath';

/**
 * БОЛОТНЫЕ ОГОНЬКИ (болотная подложка). Чистые функции без Phaser и DOM: по
 * этому описанию частицы каждый кадр ставит сцена (scenes/FieldBiomeEffects).
 *
 * Группа — ЦЕПОЧКА огоньков: первый загорается, тлеет и начинает гаснуть — в
 * этот миг рядом загорается следующий, и так три-четыре раза. Огонек — две
 * частицы: зеленый ореол и бледное ядро поверх него. Он почти стоит на месте:
 * за всю жизнь сползает по ленивой дуге меньше чем на ячейку.
 *
 * РАЗМЕР У КАЖДОГО ОГОНЬКА СВОЙ, и ореол растет вместе с ядром: мелкий огонек
 * мелок целиком. Размеры цепочки раздаются по полосам разброса (см.
 * pickSizeShares): в каждой цепочке есть и мелкий огонек, и крупный.
 *
 * Огоньки светятся (слой с наложением «свет») и горят НАД жетонами строений и
 * гор, над значками отрядов и их шевронами — так решил владелец игры. Обе
 * частицы огонька лежат на одном слое: ореол, оставшийся под жетоном, пока
 * ядро горит над ним, рвал бы огонек надвое.
 *
 * Дорожек две, огоньки у них одни и те же:
 *  - над полем — цепочка начинается на игровой ячейке там, куда смотрит
 *    камера, и шагает по полю, не уходя с него;
 *  - вокруг поля — цепочка живет над одной из точек открытой воды пейзажа и
 *    не покидает ее.
 */

const HALO = 'halo';
const CORE = 'core';

// Сколько раз искать место получше, прежде чем взять лучшее из найденных
const PLACE_ATTEMPTS = 6;

/**
 * Размеры огоньков цепочки — доли разброса: 0 — самый мелкий, 1 — самый
 * крупный. Разброс делится на столько полос, сколько в цепочке огоньков, и
 * каждому достается своя: в любой цепочке есть огонек из нижней полосы и
 * огонек из верхней — случай не соберет цепочку из одинаковых. Полосы розданы
 * в случайном порядке, место в полосе случайно: по всем цепочкам размеры
 * ложатся на разброс ровно, и средний размер — его середина
 */
const pickSizeShares = (rng, count) => {
  const bands = Array.from({length: count}, (_, index) => index);
  for (let index = count - 1; index > 0; index -= 1) {
    const other = randomInt(rng, 0, index);
    [bands[index], bands[other]] = [bands[other], bands[index]];
  }
  return bands.map((band) => (band + rng()) / count);
};

/** Смещение огонька от места рождения к доле share его жизни: ленивая дуга */
const resolveWispX = (wisp, share) => (
  wisp.x + wisp.headingX * wisp.travel * share - wisp.headingY * wisp.bend * Math.sin(Math.PI * share)
);
const resolveWispY = (wisp, share) => (
  wisp.y + wisp.headingY * wisp.travel * share + wisp.headingX * wisp.bend * Math.sin(Math.PI * share)
);

const checkIfInBox = (box, {x, y}) => x >= box.left && x <= box.right && y >= box.top && y <= box.bottom;

const createTracks = (context, params) => {
  const {hexSize, cells, coverage} = context;
  const {wisp, chain, field, margin, palettes, layer} = params;
  const anchors = margin.anchors.map((anchor, index) => ({...context.toWorldOval(anchor), index}));
  const turnRad = toRadians(chain.turnDeg);

  /**
   * Огонек: где и когда загорается, какого он размера (sizeShare — доля
   * разброса), куда сползает, как мерцает
   */
  const buildWisp = (rng, place, bornMs, sizeShare) => {
    const holdMs = randomIn(rng, wisp.holdMs);
    const heading = rng() * TWO_PI;
    const coreSize = hexSize * lerp(wisp.coreSizeFactor.min, wisp.coreSizeFactor.max, sizeShare);
    return {
      x: place.x,
      y: place.y,
      bornMs,
      // С этого возраста огонек гаснет — и загорается следующий
      fadeFromMs: wisp.fadeInMs + holdMs,
      lifeMs: wisp.fadeInMs + holdMs + wisp.fadeOutMs,
      headingX: Math.cos(heading),
      headingY: Math.sin(heading),
      travel: hexSize * randomIn(rng, wisp.travelFactor),
      bend: hexSize * randomIn(rng, wisp.bendFactor) * randomSign(rng),
      coreSize,
      // Ореол растет вместе с ядром: у мелкого огонька и свечение мелкое
      haloSize: coreSize * wisp.haloToCoreRatio,
      fastPhase: rng() * TWO_PI,
      slowPhase: rng() * TWO_PI,
      breathPhase: rng() * TWO_PI,
    };
  };

  /**
   * Цепочка от первого места: каждый следующий огонек загорается, когда
   * предыдущий начинает гаснуть, — там, куда укажет pickNextPlace
   */
  const buildChain = (rng, firstPlace, pickNextPlace) => {
    const palette = rng() < chain.coldShare ? palettes.cold : palettes.warm;
    const count = randomInt(rng, chain.count.min, chain.count.max);
    const sizeShares = pickSizeShares(rng, count);
    const particles = [];
    let place = firstPlace;
    let direction = rng() * TWO_PI;
    let bornMs = 0;
    let durationMs = 0;
    for (let index = 0; index < count; index += 1) {
      const wispParticle = buildWisp(rng, place, bornMs, sizeShares[index]);
      // Ореол — под ядром, на том же слое
      particles.push({
        ...wispParticle, kind: HALO, layer, sublayer: 0, texture: FIELD_BIOME_TEXTURES.DOT, tint: palette.halo,
      });
      particles.push({
        ...wispParticle, kind: CORE, layer, sublayer: 1, texture: FIELD_BIOME_TEXTURES.DOT, tint: palette.core,
      });
      durationMs = bornMs + wispParticle.lifeMs;
      bornMs += wispParticle.fadeFromMs;
      // Следующий загорается рядом с местом, где этот начал гаснуть
      const fadeShare = wispParticle.fadeFromMs / wispParticle.lifeMs;
      const next = pickNextPlace(
        rng, {x: resolveWispX(wispParticle, fadeShare), y: resolveWispY(wispParticle, fadeShare)}, direction
      );
      place = next.place;
      direction = next.direction;
    }
    return {durationMs, home: firstPlace, particles};
  };

  /** Место на игровой ячейке, не по ее центру: огонек — не метка на ячейке */
  const pickFieldPlace = (rng, view) => {
    const box = resolveSpawnBox(context, view, {fieldMarginFactor: 0, viewMarginFactor: 0});
    if (cells.length === 0) {
      return pickPointInBox(rng, box);
    }
    const cellsInView = cells.filter((cell) => checkIfInBox(box, cell));
    const cell = pick(rng, cellsInView.length > 0 ? cellsInView : cells);
    return {
      x: cell.x + randomRange(rng, -1, 1) * hexSize * field.offCentreFactor,
      y: cell.y + randomRange(rng, -1, 1) * hexSize * field.offCentreFactor,
    };
  };

  /** Шаг цепочки по полю: в сторону прежнего шага, не сходя с поля */
  const pickFieldStep = (rng, from, direction) => {
    let best = null;
    let bestCoverage = -1;
    for (let attempt = 0; attempt < PLACE_ATTEMPTS; attempt += 1) {
      // Первые попытки — в пределах разворота; дальше — куда угодно, лишь бы на поле
      const turn = attempt < PLACE_ATTEMPTS / 2 ? randomRange(rng, -turnRad, turnRad) : rng() * TWO_PI;
      const distance = hexSize * randomIn(rng, chain.stepFactor);
      const nextDirection = direction + turn;
      const place = {x: from.x + Math.cos(nextDirection) * distance, y: from.y + Math.sin(nextDirection) * distance};
      const placeCoverage = coverage.sample(place.x, place.y);
      if (placeCoverage > bestCoverage) {
        best = {place, direction: nextDirection};
        bestCoverage = placeCoverage;
      }
      if (placeCoverage >= 0.5) {
        break;
      }
    }
    return best;
  };

  const buildFieldGroup = ({rng, view, liveGroups}) => {
    // Цепочки не сбиваются в кучу: место подальше от уже горящих
    const apart = hexSize * field.apartFactor;
    let best = null;
    let bestDistance = -1;
    for (let attempt = 0; attempt < PLACE_ATTEMPTS; attempt += 1) {
      const place = pickFieldPlace(rng, view);
      const distance = liveGroups.reduce(
        (nearest, {home}) => Math.min(nearest, Math.hypot(home.x - place.x, home.y - place.y)), Infinity
      );
      if (distance > bestDistance) {
        best = place;
        bestDistance = distance;
      }
      if (distance >= apart) {
        break;
      }
    }
    return buildChain(rng, best, pickFieldStep);
  };

  const buildMarginGroup = ({rng, view, liveGroups}) => {
    const visible = view ? expandBox(view, hexSize * 2) : null;
    const freeAnchors = anchors.filter((anchor) => (
      coverage.sample(anchor.x, anchor.y) < margin.fieldCoverageLimit
      && !liveGroups.some((group) => group.anchorIndex === anchor.index)
    ));
    const anchor = pickWeighted(
      rng, freeAnchors, (candidate) => (!visible || checkIfInBox(visible, candidate) ? 1 : margin.offViewWeight)
    );
    if (!anchor) {
      return null;
    }
    // Цепочка не покидает свою воду: каждый огонек — в пределах точки
    const pickAnchorPlace = () => ({place: pickPointInOval(rng, anchor), direction: 0});
    return {...buildChain(rng, pickAnchorPlace().place, pickAnchorPlace), anchorIndex: anchor.index};
  };

  const resolvePose = (group, particle, elapsedMs, out) => {
    const ageMs = elapsedMs - particle.bornMs;
    if (!(ageMs > 0) || !(ageMs < particle.lifeMs)) {
      return writeHiddenPose(out);
    }
    // Разгорается и гаснет плавно
    const kindle = Math.min(
      smoothstep(0, wisp.fadeInMs, ageMs), 1 - smoothstep(particle.fadeFromMs, particle.lifeMs, ageMs)
    );
    const flicker = wisp.flicker.base
      + wisp.flicker.fast.depth * Math.sin((TWO_PI * ageMs) / wisp.flicker.fast.periodMs + particle.fastPhase)
      + wisp.flicker.slow.depth * Math.sin((TWO_PI * ageMs) / wisp.flicker.slow.periodMs + particle.slowPhase);
    const share = ageMs / particle.lifeMs;
    const x = resolveWispX(particle, share);
    const y = resolveWispY(particle, share);
    const growth = lerp(wisp.kindleScale, 1, kindle);
    if (particle.kind === HALO) {
      const breath = 1 + wisp.breath.depth * Math.sin((TWO_PI * ageMs) / wisp.breath.periodMs + particle.breathPhase);
      const size = particle.haloSize * growth * breath;
      return writePose(out, x, y, wisp.haloAlpha * kindle * flicker, size, size, 0);
    }
    const size = particle.coreSize * growth;
    return writePose(out, x, y, wisp.coreAlpha * kindle * flicker, size, size, 0);
  };

  const createSchedule = ({firstDelayMs, intervalMs, maxGroups, prewarm}) => ({
    firstDelayMs, intervalMs, maxGroups, prewarm,
  });

  return [
    {schedule: createSchedule(field), buildGroup: buildFieldGroup, resolvePose},
    {schedule: createSchedule(margin), buildGroup: buildMarginGroup, resolvePose},
  ];
};

const willOWisps = {
  kind: WILL_O_WISPS_KIND,
  // Ветер матча эффекту не нужен: при смене ветра его дорожки не трогаются
  usesWind: false,
  textures: [FIELD_BIOME_TEXTURES.DOT],
  createTracks,
};

export default willOWisps;
